import { useMemo } from "react";
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from "react-router";
import { RootLayout } from "@/app/root-layout";
import {
  RequireAuth,
  RequireSession,
  RedirectIfAuthenticated,
  isAdmin,
  isCompany,
  isNotDriver,
  canUseMoneyCodes,
} from "@/app/guards";
import type { RouteHandle } from "@/app/route-handle";
import { AppShell } from "@/components/layout/app-shell";
import { NotFoundRoute } from "@/app/not-found-route";
import { RouteError } from "@/app/route-error";
import { platformRoutes } from "@/app/platform-routes";
import { currentHost } from "@/app/tenant";
import { useSession } from "@/app/session";
import { FullScreenLoadingState } from "@/components/data/loading-state";
import { SupportMode } from "@/features/support/components/support-mode";
import { useSupportSession } from "@/features/support/hooks/use-support";

/**
 * Reproduces the Flutter app's 18 routes (`app_router.dart` — /settings was
 * dropped, then re-added as the admin CC settings page, see MIGRATION.md) as a React Router tree. The
 * nested guard layout routes (RequireAuth / RequireSession /
 * RedirectIfAuthenticated) are the same redirect table as the Flutter
 * router's single `redirect` callback, just expressed declaratively.
 *
 * Four routes that took an in-memory object through go_router's `extra`
 * (not deep-linkable, breaks on refresh) now take a real URL param instead
 * — see MIGRATION.md "Known behavioral differences":
 *   /company-form      → /companies/:companyId
 *   /card-assignment   → /companies/:companyId/cards
 *   /card-email        → "Assign Driver" dialog on /cards/:cardNumber
 *   /week-detail       → /weeks/:weekId
 *   /monthly-report-detail → /monthly-reports/:monthId
 *
 * Every feature route is lazy-loaded so the initial bundle only pays for
 * the shell + auth + whichever route the user actually lands on. `handle`
 * (breadcrumb trail — see route-handle.ts and layout/breadcrumbs.tsx) is
 * set directly on each route object here rather than in the lazy-loaded
 * page module, since it's static config with no reason to be code-split.
 */
const tenantRoutes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "update-password",
        lazy: () =>
          import("@/features/auth/routes/update-password-route").then((m) => ({
            Component: m.UpdatePasswordRoute,
          })),
      },
      {
        element: <RedirectIfAuthenticated />,
        children: [
          {
            path: "sign-in",
            lazy: () =>
              import("@/features/auth/routes/sign-in-route").then((m) => ({
                Component: m.SignInRoute,
              })),
          },
          {
            path: "password-reset",
            lazy: () =>
              import("@/features/auth/routes/password-reset-route").then((m) => ({
                Component: m.PasswordResetRoute,
              })),
          },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, element: <Navigate to="/home" replace /> },
              {
                path: "home",
                handle: { crumbs: [{ label: "Home" }] } satisfies RouteHandle,
                lazy: () =>
                  import("@/features/dashboard/routes/home-route").then((m) => ({
                    Component: m.HomeRoute,
                  })),
              },
              {
                element: <RequireSession allow={isAdmin} />,
                children: [
                  {
                    path: "companies",
                    handle: { crumbs: [{ label: "Subcontractors" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/companies/routes/companies-list-route").then((m) => ({
                        Component: m.CompaniesListRoute,
                      })),
                  },
                  {
                    path: "companies/:companyId",
                    handle: {
                      crumbs: (params) => [
                        { label: "Subcontractors", href: "/companies" },
                        { label: params.companyId === "new" ? "Add Company" : "Edit Company" },
                      ],
                    } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/companies/routes/company-form-route").then((m) => ({
                        Component: m.CompanyFormRoute,
                      })),
                  },
                  {
                    path: "companies/:companyId/cards",
                    handle: {
                      crumbs: (params) => [
                        { label: "Subcontractors", href: "/companies" },
                        { label: "Edit Company", href: `/companies/${params.companyId}` },
                        { label: "Assign Cards" },
                      ],
                    } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/companies/routes/card-assignment-route").then((m) => ({
                        Component: m.CardAssignmentRoute,
                      })),
                  },
                  {
                    path: "settings",
                    handle: { crumbs: [{ label: "Settings" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/settings/routes/settings-route").then((m) => ({
                        Component: m.SettingsRoute,
                      })),
                  },
                  {
                    path: "monthly-reports",
                    handle: { crumbs: [{ label: "Monthly Card Usage" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/monthly-reports/routes/month-list-route").then((m) => ({
                        Component: m.MonthListRoute,
                      })),
                  },
                  {
                    path: "monthly-reports/:monthId",
                    handle: {
                      crumbs: [
                        { label: "Monthly Card Usage", href: "/monthly-reports" },
                        { label: "Month Detail" },
                      ],
                    } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/monthly-reports/routes/month-detail-route").then((m) => ({
                        Component: m.MonthDetailRoute,
                      })),
                  },
                ],
              },
              {
                path: "cards",
                handle: { crumbs: [{ label: "Cards" }] } satisfies RouteHandle,
                lazy: () =>
                  import("@/features/cards/routes/cards-route").then((m) => ({
                    Component: m.CardsRoute,
                  })),
              },
              {
                path: "cards/:cardNumber",
                handle: {
                  crumbs: (params) => [{ label: "Cards", href: "/cards" }, { label: params.cardNumber ?? "Card" }],
                } satisfies RouteHandle,
                lazy: () =>
                  import("@/features/cards/routes/card-detail-route").then((m) => ({
                    Component: m.CardDetailRoute,
                  })),
              },
              {
                element: <RequireSession allow={isNotDriver} />,
                children: [
                  {
                    path: "weeks",
                    handle: { crumbs: [{ label: "Weekly Fuel Reports" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/weeks/routes/weeks-route").then((m) => ({
                        Component: m.WeeksRoute,
                      })),
                  },
                  {
                    path: "weeks/:weekId",
                    handle: {
                      crumbs: [{ label: "Weekly Fuel Reports", href: "/weeks" }, { label: "Week Detail" }],
                    } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/weeks/routes/week-detail-route").then((m) => ({
                        Component: m.WeekDetailRoute,
                      })),
                  },
                ],
              },
              {
                element: <RequireSession allow={canUseMoneyCodes} />,
                children: [
                  {
                    path: "money-codes",
                    handle: { crumbs: [{ label: "Money Codes" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/money-codes/routes/money-codes-route").then((m) => ({
                        Component: m.MoneyCodesRoute,
                      })),
                  },
                  {
                    path: "money-codes/new",
                    handle: {
                      crumbs: [{ label: "Money Codes", href: "/money-codes" }, { label: "Issue Money Code" }],
                    } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/money-codes/routes/money-code-form-route").then((m) => ({
                        Component: m.MoneyCodeFormRoute,
                      })),
                  },
                ],
              },
              {
                element: <RequireSession allow={isCompany} />,
                children: [
                  {
                    path: "map",
                    handle: { crumbs: [{ label: "Map" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/map/routes/company-map-route").then((m) => ({
                        Component: m.CompanyMapRoute,
                      })),
                  },
                ],
              },
              {
                path: "trip-route",
                handle: {
                  crumbs: [{ label: "Map", href: "/home" }, { label: "Plan a trip" }],
                } satisfies RouteHandle,
                lazy: () =>
                  import("@/features/map/routes/trip-route-route").then((m) => ({
                    Component: m.TripRouteRoute,
                  })),
              },
              { path: "*", element: <NotFoundRoute /> },
            ],
          },
        ],
      },
    ],
  },
];

export function AppRouter() {
  // One build serves every address: the admin address gets the platform console, every other
  // address the tenant app. The router is created once, for whichever applies.
  if (currentHost().kind === "platform") {
    return <PlatformAddress />;
  }
  return <TenantRouter />;
}

function TenantRouter() {
  const router = useMemo(() => createBrowserRouter(tenantRoutes), []);
  return <RouterProvider router={router} />;
}

/**
 * The admin address: the console, unless the signed-in platform admin has a live support session, in
 * which case it shows that organization's app (same routes as the organization's own address).
 */
function PlatformAddress() {
  const session = useSession();
  const support = useSupportSession(session.status === "super_admin");
  const consoleRouter = useMemo(() => createBrowserRouter(platformRoutes), []);

  if (session.status === "loading" || (session.status === "super_admin" && support.isPending)) {
    return <FullScreenLoadingState />;
  }
  if (support.data) {
    return (
      <SupportMode session={support.data}>
        <TenantRouter />
      </SupportMode>
    );
  }
  return <RouterProvider router={consoleRouter} />;
}
